import React, { JSX, useEffect, useState } from 'react';
import Constants from 'assets/utils/constant';
import LMap from './maps/LMap';
import MarkersAndClusters from './maps/MarkersAndClusters';
import Icons from 'components/atoms/Icons';
import { LatLng, LocationFields } from 'components/types/MapTypes';
import { formatLocationUrl } from 'assets/utils/formatUrl';

type LocationMapProps = {
  results?: Array<LocationFields>;
  mapResultsLoaded: boolean;
  calculatedCenter?: [number, number];
};

type LocationMapState = {
  showingInfoWindow: boolean;
  activeMarker: LatLng;
  selectedPlaces: Array<LocationFields> | null;
  selectedPlacesIndex: number;
  bounds: [number, number, number, number] | null;
  center: [number, number] | null;
  zoom: number | null;
  resultExpanded: boolean;
};

export const getMapsLink = ({
  street1,
  street2,
  suite,
  city,
  state,
  zip,
}: {
  street1?: string;
  street2?: string;
  suite?: string;
  city?: string;
  state?: string;
  zip?: string;
}) => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isAppleDevice = /iPhone|iPad|Macintosh/i.test(ua);

  const secondLine = street2 || suite || '';
  const address = encodeURIComponent(
    `${street1 ?? ''}${secondLine ? `, ${secondLine}` : ''}, ${city ?? ''}, ${
      state ?? ''
    } ${zip ?? ''}`
  );

  return isAppleDevice
    ? `https://maps.apple.com/?daddr=${address}`
    : `https://www.google.com/maps/?daddr=${address}`;
};

const DEFAULT_CENTER: [number, number] = [
  Constants.mapCenter.lng,
  Constants.mapCenter.lat,
];

const calculateBounds = (
  locations: Array<LocationFields>
): [number, number, number, number] | null => {
  const positions = locations
    .filter(result => result.latitude && result.longitude)
    .map(
      result =>
        [parseFloat(result.longitude), parseFloat(result.latitude)] as [
          number,
          number,
        ]
    );

  if (positions.length === 0) return null;

  if (positions.length === 1) {
    const [lng, lat] = positions[0];
    return [lng - 0.2, lat - 0.2, lng + 0.2, lat + 0.2];
  }

  const lngs = positions.map(([lng]) => lng);
  const lats = positions.map(([, lat]) => lat);

  return [
    Math.min(...lngs),
    Math.min(...lats),
    Math.max(...lngs),
    Math.max(...lats),
  ];
};

export const LocationMap = ({
  results = [],
  mapResultsLoaded,
  calculatedCenter,
}: LocationMapProps): JSX.Element => {
  const safeResults = Array.isArray(results) ? results : [];

  const initialState: LocationMapState = {
    showingInfoWindow: false,
    activeMarker: {},
    selectedPlaces: null,
    selectedPlacesIndex: 0,
    bounds: null,
    center: calculatedCenter || DEFAULT_CENTER,
    zoom: 8,
    resultExpanded: false,
  };

  const [state, setState] = useState<LocationMapState>(initialState);

  const resetToBounds = () => {
    const bounds = calculateBounds(safeResults);

    setState(prevState => ({
      ...prevState,
      bounds,
      showingInfoWindow: false,
      activeMarker: {},
      selectedPlaces: null,
      selectedPlacesIndex: 0,
      zoom: bounds ? null : 8,
      center: bounds
        ? null
        : calculatedCenter
          ? calculatedCenter
          : DEFAULT_CENTER,
      resultExpanded: false,
    }));
  };

  const onMarkerClick = (
    position: [number, number],
    locationsAtPoint: Array<LocationFields>
  ) => {
    const [longitude, latitude] = position;

    setState(prevState => ({
      ...prevState,
      bounds: null,
      selectedPlaces: locationsAtPoint,
      selectedPlacesIndex: 0,
      activeMarker: {
        latitude,
        longitude,
      },
      showingInfoWindow: true,
      center: [longitude, latitude],
      zoom: 15,
      resultExpanded: false,
    }));
  };

  const prevSelectedPlaces = () => {
    setState(prevState => ({
      ...prevState,
      selectedPlacesIndex: prevState.selectedPlacesIndex - 1,
    }));
  };

  const nextSelectedPlaces = () => {
    setState(prevState => ({
      ...prevState,
      selectedPlacesIndex: prevState.selectedPlacesIndex + 1,
    }));
  };

  const closeInfoWindow = () => {
    mapResultsLoaded && resetToBounds();
  };

  useEffect(() => {
    mapResultsLoaded && resetToBounds();
  }, []);

  useEffect(() => {
    mapResultsLoaded && resetToBounds();
  }, [mapResultsLoaded, safeResults]);

  const {
    activeMarker,
    bounds,
    center,
    selectedPlaces,
    selectedPlacesIndex,
    zoom,
  } = state;

  const selectedPlace = selectedPlaces?.[selectedPlacesIndex] ?? null;

  return (
    <LMap
      center={center}
      bounds={bounds}
      maxZoom={17}
      boundsMaxZoom={8}
      minZoom={4}
      zoom={zoom}
      zoomControl={true}
      mapTypeControl={false}
      fullscreenControl={false}
      style="road_shaded_relief"
    >
      {mapResultsLoaded && (
        <MarkersAndClusters
          results={safeResults}
          onMarkerClick={onMarkerClick}
          activeMarker={activeMarker}
        />
      )}

      {selectedPlace ? (
        <div
          className={
            state.resultExpanded
              ? 'search-result location-result map-result expanded'
              : 'search-result location-result map-result'
          }
          itemScope
          itemType="http://schema.org/Physician"
        >
          <div className="map-result-pagination">
            {selectedPlaces && selectedPlaces.length > 1 && (
              <button
                aria-label="Previous location at point"
                disabled={selectedPlacesIndex === 0}
                onClick={prevSelectedPlaces}
              >
                <Icons id="pld-chevron-left" aria-hidden="true" />
              </button>
            )}

            <p>
              {selectedPlacesIndex + 1} of {selectedPlaces?.length ?? 0}
            </p>

            {selectedPlaces && selectedPlaces.length > 1 && (
              <button
                aria-label="Next location at point"
                disabled={selectedPlacesIndex === selectedPlaces.length - 1}
                onClick={nextSelectedPlaces}
              >
                <Icons id="pld-chevron-right" aria-hidden="true" />
              </button>
            )}
          </div>

          <button
            className="map-result-close-btn"
            onClick={closeInfoWindow}
            aria-label={`Close info for ${selectedPlace.title}`}
          >
            <Icons id="close" className="icon icon-close" aria-hidden="true" />
          </button>

          <a
            className="result-info"
            href={formatLocationUrl(
              selectedPlace.locationid ?? '',
              selectedPlace.title ?? ''
            )}
          >
            <div className="result-details">
              <div
                className="location-address"
                itemProp="hospitalAffiliation"
                itemType="http://schema.org/Hospital"
              >
                <p className="result-name" itemProp="name">
                  {selectedPlace.title}
                </p>

                <ul>
                  <li
                    className="address result-detail"
                    itemProp="address"
                    itemScope
                    itemType="http://schema.org/PostalAddress"
                  >
                    <Icons id="pld-location-pin" />
                    <p>
                      <a
                        className="location-address track-link-click"
                        href={getMapsLink({
                          street1: selectedPlace.street1,
                          street2: selectedPlace.street2,
                          city: selectedPlace.city,
                          state: selectedPlace.state,
                          zip: selectedPlace.zip,
                        })}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {selectedPlace.street1 ?? ''}
                        {selectedPlace.street2 && (
                          <> {selectedPlace.street2},</>
                        )}
                        <>
                          {' '}
                          {selectedPlace.city ?? ''},{' '}
                          {selectedPlace.state ?? ''} {selectedPlace.zip ?? ''}
                        </>
                      </a>
                    </p>
                  </li>

                  {selectedPlace.phone && (
                    <li className="result-detail">
                      <Icons id="pld-phone" />
                      <p>
                        <a href={`tel:${selectedPlace.phone}`}>
                          {selectedPlace.phone}
                        </a>
                      </p>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </a>
        </div>
      ) : null}
    </LMap>
  );
};

export default LocationMap;
